import { lazy, Suspense } from 'react';
import { Navigate } from 'react-router-dom';
import { LoadingSpinner } from '@/components/shared/ui/LoadingSpinner';
import { withSuspense } from './utils';
import { UserAuthModalWrapper, ResetPasswordWrapper } from './wrappers';
import type { RouteConfig } from './types';

// ============================================================================
// Lazy-loaded User Pages
// ============================================================================

const Home = withSuspense(lazy(() => import('@/features/user/pages/Home')), 'home');
const ProductDetail = withSuspense(lazy(() => import('@/features/user/pages/ProductDetail')), 'product');
const Tienda = withSuspense(lazy(() => import('@/features/user/pages/Tienda/TiendaPage')), 'list');
const Checkout = withSuspense(lazy(() => import('@/features/user/pages/Checkout/CheckoutPage')));
const PaymentReturn = withSuspense(lazy(() => import('@/features/user/pages/Checkout/pages/PaymentReturnPage')));
const ThankYou = withSuspense(lazy(() => import('@/features/user/pages/Checkout/pages/ThankYouPage')));
const CheckoutCancel = withSuspense(lazy(() => import('@/features/user/pages/Checkout/pages/CheckoutCancelPage')));
const Nosotros = withSuspense(lazy(() => import('@/features/user/pages/Nosotros/NosotrosPage')));
const ProductosMasVendidos = withSuspense(lazy(() => import('@/features/user/pages/ProductosMasVendidos/ProductosMasVendidosPage')), 'list');
const Favoritos = withSuspense(lazy(() => import('@/features/user/pages/Account/FavoritosPage')));
const Contact = withSuspense(lazy(() => import('@/features/user/pages/Contact/ContactPage')));
const Perfil = withSuspense(lazy(() => import('@/features/user/pages/Account/PerfilPage')));
const AuthCallback = withSuspense(lazy(() => import('@/features/user/pages/AuthCallback/AuthCallbackPage')));
const LegalPage = withSuspense(lazy(() => import('@/features/user/pages/LegalPage/LegalPage')));
const NotFoundPage = withSuspense(lazy(() => import('@/pages/NotFoundPage')));

// ============================================================================
// Public Routes Configuration
// ============================================================================

export const publicRoutes: RouteConfig[] = [
    // Home & Main Pages
    {
        path: '/',
        element: <Home />,
        meta: { title: 'Inicio', skeletonType: 'home' }
    },

    // Shop & Products
    {
        path: '/tienda',
        element: <Tienda />,
        meta: { title: 'Tienda', skeletonType: 'list' }
    },
    {
        path: '/producto/:slug',
        element: <ProductDetail />,
        meta: { title: 'Detalle de Producto', skeletonType: 'product' }
    },
    {
        path: '/productos-mas-vendidos',
        element: <ProductosMasVendidos />,
        meta: { title: 'Productos Más Vendidos', skeletonType: 'list' }
    },

    // Checkout Flow
    {
        path: '/checkout',
        element: <Checkout />,
        meta: { title: 'Checkout' }
    },
    {
        path: '/checkout/return',
        element: <PaymentReturn />,
        meta: { title: 'Procesando Pago' }
    },
    {
        path: '/checkout/gracias',
        element: <ThankYou />,
        meta: { title: 'Gracias por tu compra' }
    },
    {
        path: '/checkout/cancel',
        element: <CheckoutCancel />,
        meta: { title: 'Pago Cancelado' }
    },

    // User Account
    {
        path: '/favoritos',
        element: <Favoritos />,
        meta: { title: 'Mis Favoritos', requiresAuth: true }
    },
    {
        path: '/perfil',
        element: <Perfil />,
        meta: { title: 'Mi Perfil', requiresAuth: true }
    },
    {
        path: '/pedidos',
        element: <Navigate to="/perfil?tab=orders" replace />,
        meta: { title: 'Mis Pedidos', requiresAuth: true }
    },

    // Authentication
    {
        path: '/auth/callback',
        element: <AuthCallback />,
        meta: { title: 'Autenticando' }
    },
    {
        path: '/user-auth',
        element: (
            <Suspense fallback={<LoadingSpinner size="sm" />}>
                <UserAuthModalWrapper />
            </Suspense>
        ),
        meta: { title: 'Iniciar Sesión' }
    },
    {
        path: '/reset-password',
        element: (
            <Suspense fallback={<LoadingSpinner size="sm" />}>
                <ResetPasswordWrapper />
            </Suspense>
        ),
        meta: { title: 'Restablecer Contraseña' }
    },

    // Static Pages
    {
        path: '/contacto',
        element: <Contact />,
        meta: { title: 'Contacto' }
    },
    {
        path: '/nosotros',
        element: <Nosotros />,
        meta: { title: 'Nosotros' }
    },

    // Legal Pages (Dynamic slug)
    {
        path: '/:slug(politica-de-privacidad|terminos-y-condiciones|preguntas-frecuentes)',
        element: <LegalPage />,
        meta: { title: 'Legal' }
    },

    // 404 - Must be last
    {
        path: '*',
        element: <NotFoundPage />,
        meta: { title: 'Página no encontrada' }
    },
];
